import { useState } from "react";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { CalendarCheck, CalendarX, Check, Loader2, Pencil, X, Mail, CheckCircle, FileText, Package } from "lucide-react";
import { registrarCorreoEnviado } from "./HistorialCorreosOC";
import { RecepcionDetalleDialog } from "./RecepcionDetalleDialog";

// Helper para parsear fechas evitando problemas de zona horaria
const parseDateLocal = (dateStr: string) => {
  if (!dateStr) return new Date();
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

interface Entrega {
  id: string;
  orden_compra_id: string;
  numero_entrega: number;
  cantidad_bultos: number;
  fecha_programada: string | null;
  status: string;
  recepcion_finalizada_en: string | null;
}

interface EntregasPopoverProps {
  orden: any;
  entregas: Entrega[];
  entregasStatus: { total: number; programadas: number; recibidas: number } | undefined;
}

const EntregasPopover = ({ orden, entregas, entregasStatus }: EntregasPopoverProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingFecha, setEditingFecha] = useState("");
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [entregaParaPDF, setEntregaParaPDF] = useState<string | null>(null);
  const [recepcionDialogOpen, setRecepcionDialogOpen] = useState(false);

  // Filter entregas for this order
  const entregasOrden = entregas.filter(e => e.orden_compra_id === orden.id);

  // For single delivery orders
  const isSingleDelivery = !orden.entregas_multiples;

  // Send notification email to supplier for scheduled deliveries
  const sendDeliveryNotificationEmail = async (scheduledEntregas: Entrega[], isDateChange: boolean = false, oldDate?: string) => {
    if (!orden.proveedores?.email) {
      console.log("No supplier email available");
      return;
    }

    try {
      // Get gmail account for sending
      const { data: gmailCuentas } = await supabase
        .from("gmail_cuentas")
        .select("id, email")
        .eq("proposito", "compras")
        .eq("activo", true)
        .limit(1);

      const gmailCuenta = gmailCuentas?.[0];
      if (!gmailCuenta) {
        console.log("No gmail account configured for compras");
        return;
      }

      const totalEntregas = entregasOrden.length;
      const programadasCount = scheduledEntregas.length;
      
      let subject: string;
      let introText: string;
      let entregasText: string;

      if (isDateChange && scheduledEntregas.length === 1 && oldDate) {
        // Single delivery date change
        const entrega = scheduledEntregas[0];
        const oldDateFormatted = format(parseDateLocal(oldDate), "dd/MM/yyyy", { locale: es });
        const newDateFormatted = format(parseDateLocal(entrega.fecha_programada!), "dd/MM/yyyy", { locale: es });
        
        subject = `[ALMASA] Cambio de fecha de entrega - ${orden.folio}`;
        introText = `Le informamos que <strong>ALMASA</strong> ha modificado la fecha de entrega #${entrega.numero_entrega} de la orden de compra:`;
        entregasText = `
          <p><strong>Fecha anterior:</strong> <span style="text-decoration: line-through; color: #999;">${oldDateFormatted}</span></p>
          <p><strong>Nueva fecha:</strong> <span style="color: #22c55e; font-weight: bold;">${newDateFormatted}</span></p>
          <p><strong>Cantidad:</strong> ${entrega.cantidad_bultos} bultos</p>
        `;
      } else {
        // New scheduling (single or multiple)
        entregasText = scheduledEntregas
          .map(e => `Entrega #${e.numero_entrega}: ${format(parseDateLocal(e.fecha_programada!), "dd/MM/yyyy", { locale: es })} (${e.cantidad_bultos} bultos)`)
          .join("<br>");

        const isPartial = programadasCount < totalEntregas;
        subject = isPartial 
          ? `[ALMASA] Programación de ${programadasCount} de ${totalEntregas} entregas - ${orden.folio}`
          : `[ALMASA] Programación de entregas - ${orden.folio}`;
        introText = `Le informamos que <strong>ALMASA</strong> ha programado ${isPartial ? `${programadasCount} de ${totalEntregas}` : "todas las"} entregas de la siguiente orden de compra:`;
        entregasText = `
          <p><strong>Entregas programadas:</strong></p>
          <div style="background: #f5f5f5; padding: 15px; border-radius: 4px;">
            ${scheduledEntregas.map(e => `Entrega #${e.numero_entrega}: ${format(parseDateLocal(e.fecha_programada!), "dd/MM/yyyy", { locale: es })} (${e.cantidad_bultos} bultos)`).join("<br>")}
          </div>
        `;
      }

      // Generate signed confirmation URL via edge function
      const { data: urlData, error: urlError } = await supabase.functions.invoke("generate-oc-confirmation-url", {
        body: {
          ordenId: orden.id,
          action: "confirm-entregas",
          entregas: scheduledEntregas.map(e => e.id),
        },
      });

      if (urlError || !urlData?.url) {
        console.error("Error generating signed URL:", urlError);
        throw new Error("No se pudo generar URL de confirmación");
      }

      const confirmUrl = urlData.url;

      const htmlBody = `
        <div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
          <div style="background: #B22234; color: white; padding: 20px; text-align: center;">
            <h1 style="margin: 0;">Abarrotes La Manita</h1>
          </div>
          <div style="padding: 30px; background: #f9f9f9;">
            <p>Estimado proveedor,</p>
            <p>${introText}</p>
            
            <div style="background: white; border: 1px solid #ddd; border-radius: 8px; padding: 20px; margin: 20px 0;">
              <h2 style="color: #B22234; margin-top: 0;">Orden de Compra: ${orden.folio}</h2>
              ${entregasText}
            </div>

            <p>Por favor confirme la recepción de esta ${isDateChange ? "modificación" : "programación"} haciendo clic en el siguiente botón:</p>
            
            <div style="text-align: center; margin: 30px 0;">
              <a href="${confirmUrl}" 
                 style="background: #22c55e; color: white; padding: 15px 30px; text-decoration: none; border-radius: 8px; font-weight: bold; display: inline-block;">
                ✓ Confirmar ${isDateChange ? "Cambio de Fecha de Entrega" : "Fecha de Entrega"}
              </a>
            </div>

            <p style="color: #666; font-size: 14px;">
              Al hacer clic en el botón, usted confirma que ha recibido y acepta ${isDateChange ? "el cambio de fecha" : "las fechas de entrega programadas"}.
            </p>
          </div>
          <div style="background: #333; color: white; padding: 15px; text-align: center; font-size: 12px;">
            <p style="margin: 0;">Abarrotes La Manita SA de CV</p>
          </div>
        </div>
      `;

      const { data: emailData, error } = await supabase.functions.invoke("gmail-api", {
        body: {
          action: "send",
          gmailCuentaId: gmailCuenta.id,
          to: orden.proveedores.email,
          subject,
          htmlBody,
        },
      });

      // Registrar correo enviado
      await registrarCorreoEnviado({
        tipo: isDateChange ? "reprogramacion" : "orden_compra",
        referencia_id: orden.id,
        destinatario: orden.proveedores.email,
        asunto: subject,
        gmail_cuenta_id: gmailCuenta.id,
        gmail_message_id: emailData?.messageId || null,
        error: error?.message || null,
        contenido_preview: isDateChange 
          ? `Notificación de cambio de fecha de entrega`
          : `Notificación de ${programadasCount} entrega(s) programada(s)`,
      });

      if (error) throw error;

      toast({
        title: isDateChange ? "Notificación de cambio enviada" : "Notificación enviada",
        description: isDateChange 
          ? "Se notificó al proveedor sobre el cambio de fecha"
          : `Se notificó al proveedor sobre ${programadasCount} entrega(s) programada(s)`,
      });
    } catch (error: any) {
      console.error("Error sending delivery notification:", error);
      // Don't show error toast - the date was saved successfully
    }
  };

  const handleSave = async (entregaId: string) => {
    if (!editingFecha) return;
    
    // Check if this is a NEW date or a DATE CHANGE
    const entregaActual = entregasOrden.find(e => e.id === entregaId);
    const fechaAnterior = entregaActual?.fecha_programada;
    const esNuevaProgramacion = !fechaAnterior;
    const esCambioDeFecha = fechaAnterior && fechaAnterior !== editingFecha;
    
    setSaving(true);
    try {
      const { error } = await supabase
        .from("ordenes_compra_entregas")
        .update({ fecha_programada: editingFecha, status: "pendiente" })
        .eq("id", entregaId);

      if (error) throw error;

      toast({
        title: "Fecha actualizada",
        description: "La fecha de entrega se actualizó correctamente",
      });

      // Refresh data
      await queryClient.invalidateQueries({ queryKey: ["ordenes_compra_entregas_all"] });
      await queryClient.invalidateQueries({ queryKey: ["entregas_calendario"] });

      // Send notification based on scenario
      if (esNuevaProgramacion) {
        // New scheduling - get all pending scheduled entregas
        const { data: updatedEntregas } = await supabase
          .from("ordenes_compra_entregas")
          .select("*")
          .eq("orden_compra_id", orden.id)
          .not("fecha_programada", "is", null)
          .eq("status", "pendiente");

        if (updatedEntregas && updatedEntregas.length > 0) {
          await sendDeliveryNotificationEmail(updatedEntregas, false);
        }
      } else if (esCambioDeFecha) {
        // Date change - notify supplier of the change
        const entregaModificada = { ...entregaActual!, fecha_programada: editingFecha };
        await sendDeliveryNotificationEmail([entregaModificada], true, fechaAnterior);
      }

      setEditingId(null);
      setEditingFecha("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleClearDate = async (entregaId: string) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("ordenes_compra_entregas")
        .update({ fecha_programada: null })
        .eq("id", entregaId);

      if (error) throw error;

      toast({
        title: "Fecha eliminada",
        description: "La fecha de entrega se eliminó correctamente",
      });

      queryClient.invalidateQueries({ queryKey: ["ordenes_compra_entregas_all"] });
      queryClient.invalidateQueries({ queryKey: ["entregas_calendario"] });
      setEditingId(null);
      setEditingFecha("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSingleDelivery = async () => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from("ordenes_compra")
        .update({ fecha_entrega_programada: editingFecha || null })
        .eq("id", orden.id);

      if (error) throw error;

      toast({
        title: editingFecha ? "Fecha actualizada" : "Fecha eliminada",
        description: editingFecha 
          ? "La fecha de entrega se actualizó correctamente"
          : "La fecha de entrega se eliminó correctamente",
      });

      queryClient.invalidateQueries({ queryKey: ["ordenes_compra"] });
      queryClient.invalidateQueries({ queryKey: ["entregas_calendario"] });
      setEditingId(null);
      setEditingFecha("");
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Determine badge display
  let badgeContent;
  let badgeVariant: "default" | "destructive" | "secondary" | "outline" = "outline";
  let badgeClassName = "";

  if (orden.entregas_multiples) {
    // Calculate status directly from entregasOrden as fallback
    const total = entregasStatus?.total ?? entregasOrden.length;
    const recibidas = entregasStatus?.recibidas ?? entregasOrden.filter(e => 
      e.status === "recibida" || e.recepcion_finalizada_en
    ).length;
    const programadas = entregasStatus?.programadas ?? entregasOrden.filter(e => 
      e.fecha_programada && e.status !== "recibida" && !e.recepcion_finalizada_en
    ).length;
    
    if (total > 0) {
      // TODAS RECIBIDAS - Verde con check
      if (recibidas === total) {
        badgeVariant = "default";
        badgeClassName = "bg-green-600 hover:bg-green-700 cursor-pointer";
        badgeContent = (
          <>
            <CheckCircle className="h-3 w-3" />
            {recibidas}/{total} Recibidas
          </>
        );
      // ALGUNAS RECIBIDAS - Mostrar progreso
      } else if (recibidas > 0) {
        badgeVariant = "default";
        badgeClassName = "bg-blue-600 hover:bg-blue-700 cursor-pointer";
        badgeContent = (
          <>
            <Package className="h-3 w-3" />
            {recibidas}/{total} Recibidas
          </>
        );
      // TODAS PROGRAMADAS (sin recepciones)
      } else if (programadas === total) {
        badgeVariant = "secondary";
        badgeClassName = "cursor-pointer";
        badgeContent = (
          <>
            <CalendarCheck className="h-3 w-3" />
            {programadas}/{total} Programadas
          </>
        );
      } else if (programadas === 0) {
        badgeVariant = "destructive";
        badgeClassName = "cursor-pointer";
        badgeContent = (
          <>
            <CalendarX className="h-3 w-3" />
            0/{total}
          </>
        );
      } else {
        badgeVariant = "secondary";
        badgeClassName = "cursor-pointer";
        badgeContent = (
          <>
            <CalendarCheck className="h-3 w-3" />
            {programadas}/{total}
          </>
        );
      }
    } else {
      badgeVariant = "outline";
      badgeClassName = "text-muted-foreground cursor-pointer";
      badgeContent = (
        <>
          <CalendarX className="h-3 w-3" />
          Sin entregas
        </>
      );
    }
  } else if (isSingleDelivery) {
    // Check if single delivery was received
    const entregaRecibidaSingle = entregasOrden.find(e => 
      e.status === "recibida" || e.recepcion_finalizada_en
    );
    
    if (entregaRecibidaSingle) {
      badgeVariant = "default";
      badgeClassName = "bg-green-600 hover:bg-green-700 cursor-pointer";
      badgeContent = (
        <>
          <CheckCircle className="h-3 w-3" />
          Recibida
        </>
      );
    } else if (orden.fecha_entrega_programada) {
      badgeVariant = "default";
      badgeClassName = "bg-green-600 hover:bg-green-700 cursor-pointer";
      badgeContent = (
        <>
          <CalendarCheck className="h-3 w-3" />
          Programada
        </>
      );
    } else {
      badgeVariant = "outline";
      badgeClassName = "text-muted-foreground cursor-pointer";
      badgeContent = (
        <>
          <CalendarX className="h-3 w-3" />
          Sin programar
        </>
      );
    }
  } else {
    badgeVariant = "outline";
    badgeClassName = "text-muted-foreground cursor-pointer";
    badgeContent = (
      <>
        <CalendarX className="h-3 w-3" />
        Sin programar
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="inline-flex">
          <Badge 
            variant={badgeVariant} 
            className={`gap-1 ${badgeClassName}`}
          >
            {badgeContent}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-3" align="start">
        <div className="space-y-3">
          <div className="font-medium text-sm">Fechas de Entrega - {orden.folio}</div>
          
          {isSingleDelivery ? (
            // Single delivery order
            (() => {
              const entregaRecibidaSingle = entregasOrden.find(e => 
                e.status === "recibida" || e.recepcion_finalizada_en
              );
              
              return (
                <div className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                  <div className="flex-1">
                    <div className="text-sm font-medium flex items-center gap-2">
                      Entrega única
                      {entregaRecibidaSingle && (
                        <Badge variant="outline" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Recibida
                        </Badge>
                      )}
                    </div>
                    
                    {entregaRecibidaSingle ? (
                      // Already received - show date and PDF button
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          Recibida el {format(new Date(entregaRecibidaSingle.recepcion_finalizada_en!), "dd/MM/yyyy HH:mm", { locale: es })}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs gap-1"
                          onClick={() => {
                            setEntregaParaPDF(entregaRecibidaSingle.id);
                            setRecepcionDialogOpen(true);
                          }}
                        >
                          <FileText className="h-3 w-3" />
                          Ver Recepción
                        </Button>
                      </div>
                    ) : editingId === "single" ? (
                      <div className="flex items-center gap-2 mt-1">
                        <Input
                          type="date"
                          value={editingFecha}
                          onChange={(e) => setEditingFecha(e.target.value)}
                          className="h-8 text-sm"
                        />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={handleSaveSingleDelivery}
                          disabled={saving}
                        >
                          {saving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Check className="h-4 w-4 text-green-600" />
                          )}
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditingFecha("");
                            handleSaveSingleDelivery();
                          }}
                          disabled={saving}
                        >
                          <X className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-sm text-muted-foreground">
                        {orden.fecha_entrega_programada 
                            ? format(parseDateLocal(orden.fecha_entrega_programada), "dd/MM/yyyy")
                            : "Sin fecha"}
                        </span>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={() => {
                            setEditingId("single");
                            setEditingFecha(orden.fecha_entrega_programada || "");
                          }}
                        >
                          <Pencil className="h-3 w-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()
          ) : entregasOrden.length > 0 ? (
            // Multiple deliveries
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {entregasOrden
                .sort((a, b) => a.numero_entrega - b.numero_entrega)
                .map((entrega) => {
                  const esRecibida = entrega.status === "recibida" || entrega.recepcion_finalizada_en;
                  
                  return (
                    <div 
                      key={entrega.id} 
                      className={`flex items-center justify-between p-2 rounded-md ${
                        esRecibida ? "bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800" : "bg-muted/50"
                      }`}
                    >
                      <div className="flex-1">
                        <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                          Entrega #{entrega.numero_entrega}
                          <span className="font-normal text-muted-foreground">
                            ({entrega.cantidad_bultos} bultos)
                          </span>
                          {esRecibida && (
                            <Badge variant="outline" className="text-xs bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 border-green-300 dark:border-green-700">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Recibida
                            </Badge>
                          )}
                          {!esRecibida && entrega.status === "confirmado" && (
                            <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-700">
                              <Check className="h-3 w-3 mr-1" />
                              Confirmada
                            </Badge>
                          )}
                        </div>
                        
                        {/* Si está recibida, mostrar fecha y botón PDF */}
                        {esRecibida ? (
                          <div className="flex items-center gap-2 mt-1 flex-wrap">
                            <span className="text-xs text-green-600 dark:text-green-400">
                              Recibida el {entrega.recepcion_finalizada_en 
                                ? format(new Date(entrega.recepcion_finalizada_en), "dd/MM/yyyy HH:mm")
                                : "—"}
                            </span>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-6 text-xs"
                              onClick={() => {
                                setEntregaParaPDF(entrega.id);
                                setRecepcionDialogOpen(true);
                              }}
                            >
                              <FileText className="h-3 w-3 mr-1" />
                              Ver PDF
                            </Button>
                          </div>
                        ) : editingId === entrega.id ? (
                          <div className="flex items-center gap-2 mt-1">
                            <Input
                              type="date"
                              value={editingFecha}
                              onChange={(e) => setEditingFecha(e.target.value)}
                              className="h-8 text-sm"
                            />
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => handleSave(entrega.id)}
                              disabled={saving}
                            >
                              {saving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => handleClearDate(entrega.id)}
                              disabled={saving}
                            >
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-sm text-muted-foreground">
                              {entrega.fecha_programada 
                                ? format(parseDateLocal(entrega.fecha_programada), "dd MMM yyyy", { locale: es })
                                : "Sin fecha"}
                            </span>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-6 w-6"
                              onClick={() => {
                                setEditingId(entrega.id);
                                setEditingFecha(entrega.fecha_programada || "");
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                      
                      {/* Icono de estado */}
                      {esRecibida ? (
                        <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                      ) : entrega.fecha_programada ? (
                        <CalendarCheck className="h-4 w-4 text-amber-500 flex-shrink-0" />
                      ) : (
                        <CalendarX className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                      )}
                    </div>
                  );
                })}
            </div>
          ) : (
            <div className="text-sm text-muted-foreground py-2">
              No hay entregas registradas
            </div>
          )}

          {!isSingleDelivery && orden.proveedores?.email && (
            <div className="text-xs text-muted-foreground pt-2 border-t flex items-center gap-1">
              <Mail className="h-3 w-3" />
              Al guardar fechas se notificará automáticamente al proveedor
            </div>
          )}
        </div>
      </PopoverContent>
      
      {/* Dialog para ver PDF de recepción */}
      <RecepcionDetalleDialog
        entregaId={entregaParaPDF}
        open={recepcionDialogOpen}
        onOpenChange={(open) => {
          setRecepcionDialogOpen(open);
          if (!open) setEntregaParaPDF(null);
        }}
      />
    </Popover>
  );
};

export default EntregasPopover;
